<template>
  <SearchCombobox.Root
    v-model="subscriberId"
    v-model:query="searchQuery"
    :pending="subscriberList.isFetching.value"
    :options="options"
    align="end"
  >
    <SearchCombobox.Trigger as-child>
      <Tooltip.Hint text="Select a subscriber to preview their email.">
        <Menu.TriggerButton>
          {{ triggerLabel }}
        </Menu.TriggerButton>
      </Tooltip.Hint>
    </SearchCombobox.Trigger>
    <SearchCombobox.Content>
      <template #empty-state>
        <div class="flex h-20 flex-col items-center justify-center">
          <p class="text-sm text-gray-400">
            {{ searchQuery.trim() ? "No results found" : "No subscribers" }}
          </p>
        </div>
      </template>
    </SearchCombobox.Content>
  </SearchCombobox.Root>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Menu from "@/design_system/Menu";
import SearchCombobox from "@/design_system/SearchCombobox";
import Tooltip from "@/design_system/Tooltip";
import { useSubscribersListingQuery } from "@/lib/api/queries/subscribers/list";

const subscriberId = defineModel<string | undefined>();

const searchQuery = ref("");

const subscriberList = useSubscribersListingQuery({
  parameters: computed(() => {
    const trimmed = searchQuery.value.trim();
    return {
      email_address: trimmed ? [trimmed] : undefined,
    };
  }),
  ordering: "-creation_date",
  maintainData: true,
});

const subscribers = computed(() => {
  // Searching → show all results; otherwise cap to 10 most recent.
  const limit = searchQuery.value.trim() ? undefined : 10;
  return limit
    ? subscriberList.flattenedData.value.slice(0, limit)
    : subscriberList.flattenedData.value;
});

const options = computed(() =>
  subscribers.value.map((s) => ({
    id: s.id,
    label: s.email_address || "Subscriber",
  }))
);

const triggerLabel = computed(() => {
  const id = subscriberId.value;
  if (!id) {
    return "Subscriber";
  }
  return (
    subscriberList.flattenedData.value.find((s) => s.id === id)
      ?.email_address || "Subscriber"
  );
});
</script>
